import React, { useCallback } from 'react';

import { track } from '@adsk/acs-web-analytics';
import { EntitlementsUser } from '@adsk/alloy-provider';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import { StylableComponent } from '@adsk/alloy-react-theme';

import { EVENTS } from './helpers/const';
import { getTrackingProperties } from './helpers/utils';
import { useUserSubscriptions, useTrialMessage } from './hooks';
import TrialBannerView from './TrialBannerView';

type TrialBannerProps = StylableComponent<HTMLDivElement> & {
  user?: Omit<EntitlementsUser, 'locale' | 'licenses'> | null;
  currentProductId?: string;
};

/**
 * A TrialBanner
 * Trial banner allow to show messages and options to manage the subscriptions of the user.
 */

const TrialBanner = React.forwardRef<HTMLDivElement, TrialBannerProps>(
  (
    { user = null, currentProductId, ...props }: TrialBannerProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { userTrialSubscriptions, showBanner } = useUserSubscriptions();

    const trialMessage = useTrialMessage({ userTrialSubscriptions });

    const trackOpenModal = useCallback(() => {
      if (!user || !userTrialSubscriptions) return null;

      return track(EVENTS.OPEN, {
        ...getTrackingProperties(
          user,
          userTrialSubscriptions,
          currentProductId ? currentProductId : 'projects'
        ),
      });
    }, [currentProductId, user, userTrialSubscriptions]);

    return showBanner ? (
      <TrialBannerView
        {...props}
        ref={ref}
        message={showBanner ? trialMessage : null}
        userTrials={userTrialSubscriptions ?? []}
        displayButton={showBanner}
        onClickAnalytics={trackOpenModal}
        data-testid="TrialBanner"
      />
    ) : null;
  }
);

TrialBanner.displayName = 'TrialBanner';

const Banner = TrialBanner;

// Wrap with suspense
const SuspendedTrialBanner = WithAlloySuspense(Banner);

// Manually copy static properties
SuspendedTrialBanner.propTypes = Banner.propTypes;
SuspendedTrialBanner.defaultProps = Banner.defaultProps;

export default SuspendedTrialBanner;
